import html2canvas from 'html2canvas';
import { fetchSocial } from "../api/ai_social_content/ai_social";

/**
 * Generate default sharing text based on claim and verdict
 */
const generateDefaultShareText = (claim, verdict, platform, url) => {
  const verdictEmoji = 
    verdict === "Valid" ? "✅" : 
    verdict === "Invalid" ? "❌" : 
    verdict === "Ambiguous" ? "⚖️" : "🔍";
  
  const maxClaimLength = 80;
  const shortenedClaim = claim.length > maxClaimLength 
    ? claim.substring(0, maxClaimLength) + '...' 
    : claim;
  
  // Customize text based on platform
  if (platform === 'facebook') {
    return `${verdictEmoji} Fact Check: "${shortenedClaim}"\n\nVerdict: ${verdict}\n\nI used True Vote to analyze parliamentary records and determine if this political claim is accurate.\n\nSee the full analysis: ${url}`;
  } else if (platform === 'instagram') {
    return `${verdictEmoji} Fact Check: "${shortenedClaim}"\n\nVerdict: ${verdict}\n\nI analyzed parliamentary records and found this claim to be ${verdict.toLowerCase()}.\n\nSee the full analysis: ${url}\n\n#factcheck #politics #truthmatters #democracymatters`;
  } else { // Twitter/X default
    return `${verdictEmoji} Fact-check: "${shortenedClaim}" is ${verdict.toLowerCase()} based on parliament records.`;
  }
};

/**
 * Fetch AI-generated social content for the specified platform
 */
const getSocialContent = async (resultData, platform, url) => {
  try {
    // Include the platform in the request
    const socialContent = await fetchSocial(resultData, platform, url);
    console.log(`${platform} social content data:`, socialContent);
    
    if (!socialContent) {
      throw new Error(`${platform} content not found or invalid data returned`);
    }
    
    return socialContent;
  } catch (err) {
    console.error(`Error fetching ${platform} content:`, err);
    // Return fallback text
    return generateDefaultShareText(
      resultData.speech_verdict?.claim_analyzed || '',
      resultData.overall_verdict || '',
      platform,
      url
    );
  } 
};

/**
 * Capture a specific element from the result page as an image
 */
export const captureResultAsImage = async (elementId) => {
  try {
    const element = document.getElementById(elementId);
    
    if (!element) {
      console.error(`Element with ID "${elementId}" not found`);
      return null;
    }
    
    // Use html2canvas to capture the element
    const canvas = await html2canvas(element, {
      scale: 2, // Higher scale for better quality
      useCORS: true, // Allow images from other domains
      backgroundColor: '#ffffff',
      logging: false
    });
    
    // Convert to data URL
    const imageDataUrl = canvas.toDataURL('image/png');
    return imageDataUrl;
  } catch (error) {
    console.error('Error capturing result as image:', error);
    return null;
  }
};

/**
 * Download the generated image to the user's device
 */
export const downloadImage = (dataUrl, filename = 'claim-fact-check.png') => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Share to Twitter/X
 */
export const shareToTwitter = async (claim, verdict, resultData, url) => {
  try {
    // Get Twitter-specific social content
    const socialText = await getSocialContent(resultData, 'twitter', url);
    
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(socialText)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank');
    
    return true;
  } catch (error) {
    console.error('Error in shareToTwitter:', error);
    
    // Fallback to basic share if something goes wrong
    const fallbackText = generateDefaultShareText(claim, verdict, 'twitter', url);
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(fallbackText)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank');
    
    return true;
  }
};

/**
 * Open Facebook sharing dialog
 */
export const openFacebookShare = (url) => {
  const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  window.open(shareUrl, '_blank');
  return true;
};

/**
 * Prepare content for Facebook
 * Returns text for dialog + opens share dialog if requested
 */
export const shareToFacebook = async (claim, verdict, resultData, url, openShareDialog = false) => {
  try {
    // Get Facebook-specific social content
    const socialText = await getSocialContent(resultData, 'facebook', url);
    
    // Open Facebook share dialog if requested
    if (openShareDialog) {
      openFacebookShare(url);
    }
    
    // Return the Facebook text for the dialog
    return {
      success: true,
      textToCopy: socialText,
      url: url
    };
  } catch (error) {
    console.error('Error in shareToFacebook:', error);
    
    // Generate fallback text
    const fallbackText = generateDefaultShareText(claim, verdict, 'facebook', url);
    
    // Open Facebook share dialog if requested
    if (openShareDialog) {
      openFacebookShare(url);
    }
    
    // Return fallback text
    return {
      success: false,
      textToCopy: fallbackText,
      url: url,
      error: error.message
    };
  }
};

/**
 * Prepare content for Instagram
 * Note: Instagram doesn't have a direct web share API
 * This function will download the image and return text to copy
 */
export const shareToInstagram = async (claim, verdict, resultData, url, elementId) => {
  try {
    // Get Instagram-specific social content
    const socialText = await getSocialContent(resultData, 'instagram', url);
    
    // Capture image - this is required for Instagram
    const imageDataUrl = await captureResultAsImage(elementId);
    
    if (!imageDataUrl) {
      throw new Error('Failed to capture image for Instagram');
    }
    
    // Download the image
    downloadImage(imageDataUrl, 'instagram-fact-check.png');
    
    // Return the text for the app to display in a copy dialog
    return {
      success: true,
      imageDownloaded: true,
      textToCopy: socialText,
      url: url
    };
  } catch (error) {
    console.error('Error in shareToInstagram:', error);
    
    // Try to capture image again if it failed
    try {
      const imageDataUrl = await captureResultAsImage(elementId);
      if (imageDataUrl) {
        downloadImage(imageDataUrl, 'instagram-fact-check.png');
      }
    } catch (e) {
      console.error('Second attempt to capture image failed:', e);
    }
    
    // Return fallback text
    return {
      success: false,
      imageDownloaded: false,
      textToCopy: generateDefaultShareText(claim, verdict, 'instagram', url),
      url: url,
      error: error.message
    };
  }
};

/**
 * Main function to share claim result to social media
 */
export const shareToSocial = async (platform, elementId, claim, verdict, resultData, url) => {
  try {
    // Handle different platforms
    switch (platform) {
      case 'twitter':
      case 'x':
        // Capture and download image first
        const imageDataUrl = await captureResultAsImage(elementId);
        if (imageDataUrl) {
          downloadImage(imageDataUrl, 'twitter-fact-check.png');
        }
        
        // Small delay to ensure image downloads before Twitter opens
        setTimeout(async () => {
          await shareToTwitter(claim, verdict, resultData, url);
        }, 500);
        
        return { 
          success: true, 
          platform: 'twitter', 
          imageDownloaded: !!imageDataUrl 
        };
        
      case 'facebook':
        // Get Facebook sharing text and optionally open the sharing dialog
        const facebookImageUrl = await captureResultAsImage(elementId);
        if (facebookImageUrl) {
          downloadImage(facebookImageUrl, 'facebook-fact-check.png');
        }
        
        return await shareToFacebook(claim, verdict, resultData, url, false);
        
      case 'facebook-share':
        // This option opens the Facebook share dialog directly
        openFacebookShare(url);
        return { 
          success: true, 
          platform: 'facebook',
          action: 'share'
        };
        
      case 'instagram':
        // Instagram requires special handling with an image and copyable text
        return await shareToInstagram(claim, verdict, resultData, url, elementId);
        
      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }
  } catch (error) {
    console.error(`Error sharing to ${platform}:`, error);
    return { 
      success: false, 
      platform,
      error: error.message
    };
  }
};

export default shareToSocial;